import type { Observable } from 'rxjs';
import { BehaviorSubject, catchError, EMPTY } from 'rxjs';

export type BundleConfig<T> = {
  name: T;
  url: string;
};

export type Status = {
  error: boolean;
  loaded: boolean;
  loading: boolean;
};

export class ExternalBundle<_T> {
  public status$: Observable<Status>;
  private statusSubject$: BehaviorSubject<Status> = new BehaviorSubject<Status>({
    error: false,
    loaded: false,
    loading: false,
  });

  constructor(
    private url: string,
    private documentRef: Document,
  ) {
    this.status$ = this.statusSubject$.asObservable().pipe(
      catchError(() => {
        console.error(`External bundle failed to load: ${this.url}`);
        return EMPTY;
      }),
    );
  }

  public load(): Observable<Status> {
    if (this.statusSubject$.getValue().loaded || this.statusSubject$.getValue().loading)
      return this.status$;

    this.statusSubject$.next({
      ...this.statusSubject$.getValue(),
      loading: true,
    });

    const script = this.documentRef.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.defer = true;
    script.src = this.url;

    script.onload = () => {
      this.statusSubject$.next({
        error: false,
        loaded: true,
        loading: false,
      });
    };

    script.onerror = () => {
      this.statusSubject$.error({
        error: true,
        loaded: false,
        loading: false,
      });
    };

    this.documentRef.body.appendChild(script);

    return this.status$;
  }
}

export abstract class ExternalBundlesLoader<T extends string> {
  private activeBundlesMap: Map<T, ExternalBundle<T>> = new Map();

  constructor(public document: Document) {}

  public create(config: BundleConfig<T>): ExternalBundle<T> {
    const bundleInstance = externalBundleFactory(config, this.document);
    this.activeBundlesMap.set(config.name, bundleInstance);
    return bundleInstance;
  }

  public getInstance(name: T): ExternalBundle<T> | undefined {
    return this.activeBundlesMap.get(name);
  }
}

function externalBundleFactory(
  config: BundleConfig<string>,
  documentRef: Document,
): ExternalBundle<string> {
  return new ExternalBundle(config.url, documentRef);
}
